import React, { useState } from 'react';
import { XIcon, ImageIcon } from 'lucide-react';
import { ListingType, LISTING_PRICES, LISTING_TYPE_LABELS } from '../types';
import { useAuth } from '../context/AuthContext';
interface CreateListingFormProps {
  onClose: () => void;
  onSuccess: () => void;
}
export function CreateListingForm({
  onClose,
  onSuccess
}: CreateListingFormProps) {
  const {
    user,
    createListing
  } = useAuth();
  const [formData, setFormData] = useState({
    type: 'service' as ListingType,
    title: '',
    description: '',
    image: '',
    price: '',
    location: 'Nakuru, Kenya',
    eventDate: '',
    eventTime: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const listingCost = LISTING_PRICES[formData.type];
  const canAfford = user ? user.walletBalance >= listingCost : false;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.title || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }
    const result = createListing({
      type: formData.type,
      title: formData.title,
      description: formData.description,
      image: formData.image || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop',
      price: formData.price ? parseInt(formData.price) : undefined,
      location: formData.location,
      eventDate: formData.eventDate || undefined,
      eventTime: formData.eventTime || undefined
    });
    if (result.success) {
      setSuccess(result.message);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } else {
      setError(result.message);
    }
  };
  const sampleImages = ['https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=300&fit=crop', 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop'];
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-27">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-28">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between" data-id="element-29">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-30">
            Create New Listing
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close" data-id="element-31">
            <XIcon className="w-6 h-6" data-id="element-32" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-33">
          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm" data-id="element-34">
              {error}
            </div>}
          {success && <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm" data-id="element-35">
              {success}
            </div>}

          {/* Listing Type */}
          <div data-id="element-36">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-37">
              Listing Type *
            </label>
            <select value={formData.type} onChange={e => setFormData({
            ...formData,
            type: e.target.value as ListingType
          })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-38">
              {Object.entries(LISTING_TYPE_LABELS).map(([value, label]) => <option key={value} value={value} data-id="element-39">
                  {label}{' '}
                  {LISTING_PRICES[value as ListingType] > 0 ? `(${LISTING_PRICES[value as ListingType]} KES)` : '(Free)'}
                </option>)}
            </select>
          </div>

          {/* Cost Display */}
          <div className={`p-3 rounded-lg ${listingCost > 0 ? canAfford ? 'bg-green-50' : 'bg-red-50' : 'bg-blue-50'}`} data-id="element-40">
            <p className={`text-sm font-medium ${listingCost > 0 ? canAfford ? 'text-green-700' : 'text-red-700' : 'text-blue-700'}`} data-id="element-41">
              {listingCost > 0 ? <>
                  Cost: {listingCost} KES • Your balance: {user?.walletBalance}{' '}
                  KES
                  {!canAfford && ' (Insufficient funds)'}
                </> : 'This listing type is free!'}
            </p>
          </div>

          {/* Title */}
          <div data-id="element-42">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-43">
              Title *
            </label>
            <input type="text" value={formData.title} onChange={e => setFormData({
            ...formData,
            title: e.target.value
          })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter listing title" required data-id="element-44" />
          </div>

          {/* Description */}
          <div data-id="element-45">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-46">
              Description *
            </label>
            <textarea value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={3} placeholder="Describe your listing" required data-id="element-47" />
          </div>

          {/* Image Selection */}
          <div data-id="element-48">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-49">
              <ImageIcon className="w-4 h-4 inline mr-1" data-id="element-50" />
              Select Image
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-51">
              {sampleImages.map((img, idx) => <button key={idx} type="button" onClick={() => setFormData({
              ...formData,
              image: img
            })} className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${formData.image === img ? 'border-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-52">
                  <img src={img} alt={`Option ${idx + 1}`} className="w-full h-full object-cover" data-id="element-53" />
                </button>)}
            </div>
          </div>

          {/* Price (for goods/products) */}
          {(formData.type === 'good' || formData.type === 'product') && <div data-id="element-54">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-55">
                Price (KES)
              </label>
              <input type="number" value={formData.price} onChange={e => setFormData({
            ...formData,
            price: e.target.value
          })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter price" data-id="element-56" />
            </div>}

          {/* Location */}
          <div data-id="element-57">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-58">
              Location
            </label>
            <input type="text" value={formData.location} onChange={e => setFormData({
            ...formData,
            location: e.target.value
          })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter location" data-id="element-59" />
          </div>

          {/* Event Date/Time */}
          {formData.type === 'event' && <div className="grid grid-cols-2 gap-4" data-id="element-60">
              <div data-id="element-61">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-62">
                  Event Date
                </label>
                <input type="date" value={formData.eventDate} onChange={e => setFormData({
              ...formData,
              eventDate: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-63" />
              </div>
              <div data-id="element-64">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-65">
                  Event Time
                </label>
                <input type="time" value={formData.eventTime} onChange={e => setFormData({
              ...formData,
              eventTime: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-66" />
              </div>
            </div>}

          {/* Submit Button */}
          <button type="submit" disabled={listingCost > 0 && !canAfford} className={`w-full py-3 rounded-lg font-medium transition-colors ${listingCost > 0 && !canAfford ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-id="element-67">
            {listingCost > 0 ? `Create Listing (${listingCost} KES)` : 'Create Free Listing'}
          </button>
        </form>
      </div>
    </div>;
}